import React, { useState } from 'react';
import { db, storage } from '../firebase/config';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function ApplicationForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    portfolio: '',
    coverLetter: '',
    availability: '',
    salary: '',
    resume: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMessage(null);

    try {
      // Basic validation for resume file
      const file = formData.resume;
      if (!file) {
        throw new Error('Please attach your resume.');
      }
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload PDF, DOC, or DOCX.');
      }
      const maxSizeBytes = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSizeBytes) {
        throw new Error('File too large. Maximum size is 5MB.');
      }

      // 1) Upload resume to Storage
      const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
      const timestamp = Date.now();
      const storagePath = `applications/${timestamp}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const resumeUrl = await getDownloadURL(storageRef);

      // 2) Save application details to Firestore
      const applicationsCol = collection(db, 'applications');
      await addDoc(applicationsCol, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        position: formData.position,
        experience: formData.experience,
        portfolio: formData.portfolio || '',
        coverLetter: formData.coverLetter || '',
        availability: formData.availability,
        salary: formData.salary || '',
        resumeUrl,
        resumePath: storagePath,
        createdAt: serverTimestamp()
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        portfolio: '',
        coverLetter: '',
        availability: '',
        salary: '',
        resume: null
      });

      setSubmitMessage('Application submitted successfully! We\'ll get back to you soon.');

    } catch (error) {
      console.error("Error submitting application:", error);
      setSubmitMessage(error?.message || 'Error submitting application. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="application-form-container">
      <div className="form-header">
        <h4>Ready to Join Our Team?</h4>
        <p>Fill out the application form below and let's create something amazing together.</p>
      </div>

      <form className="app-form" onSubmit={handleSubmit}>
        {submitMessage && (
          <div className={`submit-message ${submitMessage.includes('successfully') ? 'success' : 'error'}`}>
            {submitMessage}
          </div>
        )}

        <div className="form-section">
          <h5>Personal Information</h5>
          <div className="form-row">
            <div className="form-group">
              <label>Full Name *</label>
              <input 
                type="text" 
                placeholder="Enter your full name" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required 
              />
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input 
                type="email" 
                placeholder="your.email@example.com" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required 
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone Number</label>
              <input 
                type="tel" 
                placeholder="+966 50 123 4567" 
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Position Applied For *</label>
              <select 
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a position</option>
                <option value="AI Game Designer">AI Game Designer</option>
                <option value="Narrative Designer">Narrative Designer</option>
                <option value="3D Artist">3D Artist</option>
                <option value="Sound Designer">Sound Designer</option>
                <option value="Game Programmer">Game Programmer</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h5>Professional Information</h5>
          <div className="form-row">
            <div className="form-group">
              <label>Years of Experience *</label>
              <select 
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                required
              >
                <option value="">Select experience level</option>
                <option value="0-1 years">0-1 years</option>
                <option value="2-3 years">2-3 years</option>
                <option value="4-5 years">4-5 years</option>
                <option value="6+ years">6+ years</option>
              </select>
            </div>
            <div className="form-group">
              <label>Portfolio/LinkedIn</label>
              <input 
                type="url" 
                placeholder="https://yourportfolio.com" 
                name="portfolio"
                value={formData.portfolio}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Expected Salary Range</label>
              <select 
                name="salary"
                value={formData.salary}
                onChange={handleInputChange}
              >
                <option value="">Select salary range</option>
                <option value="Under 10,000 SAR">Under 10,000 SAR</option>
                <option value="10,000 - 15,000 SAR">10,000 - 15,000 SAR</option>
                <option value="15,000 - 20,000 SAR">15,000 - 20,000 SAR</option>
                <option value="20,000+ SAR">20,000+ SAR</option>
                <option value="Negotiable">Negotiable</option>
              </select>
            </div>
            <div className="form-group">
              <label>Availability *</label>
              <select 
                name="availability"
                value={formData.availability}
                onChange={handleInputChange}
                required
              >
                <option value="">When can you start?</option>
                <option value="Immediately">Immediately</option>
                <option value="2 weeks notice">2 weeks notice</option>
                <option value="1 month notice">1 month notice</option>
                <option value="2+ months">2+ months</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h5>Additional Information</h5>
          <div className="form-group full-width">
            <label>Cover Letter</label>
            <textarea 
              placeholder="Tell us why you want to join Studio13 and what makes you a great fit for this role..."
              name="coverLetter"
              value={formData.coverLetter}
              onChange={handleInputChange}
              rows="4"
            />
          </div>
          <div className="form-group full-width">
            <label>Resume/CV *</label>
            <input 
              type="file" 
              accept=".pdf,.doc,.docx" 
              name="resume"
              onChange={handleInputChange}
              required
              title="Accepted file types: PDF, DOC, DOCX (Max 5MB)"
            />
            <small>Accepted formats: PDF, DOC, DOCX (Maximum file size: 5MB)</small>
          </div>
        </div>

        <div className="form-actions">
          <button className="apply-btn" type="submit" disabled={submitting}>
            {submitting ? 'Submitting Application...' : 'Submit Application'}
          </button>
        </div>
      </form>
    </div>
  );
}
